// © 2026 Holmes Computer Consultants. All rights reserved.
import { BufferAttribute, BufferGeometry, Group, Mesh, Matrix4 } from 'three'
import type asset from '../../SourceArt/PierHardware/mesh.json'
import { PartPalette, type Finish } from './partsKit'
import bumperFinish from '../../SourceArt/PierHardware/finish-06.json'

let data: typeof asset | undefined
export async function prepareAuthoredHardware(): Promise<void> {
  data ??= (await import('../../SourceArt/PierHardware/mesh.json')).default
}
type Family = keyof typeof asset.families
export const authoredHardwareFor = (machineId: string): boolean => data?.machineIds.includes(machineId) ?? false
/** Same compact Blender export as the native adapter; coordinates are playfield XY / height Z. */
function addFamily(group: Group, family: Family, id: string, sx: number, sy: number,
  bearing: number, x: number, y: number, palette: PartPalette, own: (geometry: BufferGeometry) => void): void {
  if (data === undefined) throw new Error('Authored hardware must be prepared before construction')
  const transform = new Matrix4().makeRotationZ(bearing)
    .multiply(new Matrix4().makeScale(sx, sy, 1))
  transform.setPosition(x, y, 0)
  for (const part of data.families[family]) {
    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(part.positions), 3))
    geometry.setAttribute('normal', new BufferAttribute(new Float32Array(part.normals), 3))
    geometry.setIndex(part.triangles)
    const bumper=family==='bumper-fixed'||family==='bumper-driven'
    if(bumper && part.finish==='HardwareRubber') {
      const uv=new Float32Array(part.positions.length/3*2)
      for(let v=0;v<part.positions.length/3;v++)uv[v*2+1]=(part.positions[v*3+2]!-.001)/bumperFinish.skirt.edgeHeight
      geometry.setAttribute('uv',new BufferAttribute(uv,2))
    }
    geometry.applyMatrix4(transform)
    geometry.computeBoundingBox(); geometry.computeBoundingSphere()
    own(geometry)
    const finish = (part.finish[0]!.toLowerCase() + part.finish.slice(1)) as Finish
    const mesh = new Mesh(geometry, bumper ? palette.bumperSurface(finish) : palette.get(finish))
    mesh.name = `${id}.authored.${part.finish}`
    group.add(mesh)
  }
}
function removeMeshes(group: Group): void {
  for (const child of [...group.children]) if (child instanceof Mesh) group.remove(child)
}
export function authoredBumper(group: Group, driven: Group, radius: number, id: string,
  palette: PartPalette, own: (geometry: BufferGeometry) => void): void {
  if (data === undefined) throw new Error('Authored hardware must be prepared before construction')
  removeMeshes(group); removeMeshes(driven)
  const scale = radius / data.referenceBumperRadius
  addFamily(group, 'bumper-fixed', id, scale, scale, 0, 0, 0, palette, own)
  addFamily(driven, 'bumper-driven', `${id}.driven`, scale, scale, 0, 0, 0, palette, own)
}
export function authoredTarget(group: Group, a: readonly number[], b: readonly number[], id: string,
  palette: PartPalette, own: (geometry: BufferGeometry) => void): Group {
  if (data === undefined) throw new Error('Authored hardware must be prepared before construction')
  const dx = b[0]! - a[0]!, dy = b[1]! - a[1]!, width = Math.hypot(dx, dy)
  if (width <= 0) return group
  removeMeshes(group)
  const bearing = Math.atan2(dy, dx)
  addFamily(group, 'target', id, width / data.referenceTargetWidth, 1, bearing,
    (a[0]! + b[0]!) / 2, (a[1]! + b[1]!) / 2, palette, own)
  // Existing animator retains its moving group; fitted board guide is a stationary sibling.
  const assembly = new Group(); assembly.name = id
  group.name = `${id}.driven`; assembly.add(group)
  addFamily(assembly, 'target-mount', `${id}.mount`, width / data.referenceTargetWidth, 1, bearing,
    (a[0]! + b[0]!) / 2, (a[1]! + b[1]!) / 2, palette, own)
  return assembly
}
